import { useData } from "@/context/DataContext";
import { MILESTONES } from "@/constants/milestones";
import { FREE_MODULE_IDS, useAccess } from "@/lib/access";
import { LockedPanel } from "@/components/Locked";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Circle, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const videoSrc = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) {
    const token = localStorage.getItem("smash_token");
    return `${BACKEND}${url}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  }
  return url;
};

const LessonVideo = ({ step }) => {
  const src = videoSrc(step.videoUrl);
  const webm = step.videoUrl.startsWith("/brand/") && step.videoUrl.endsWith(".mp4")
    ? step.videoUrl.replace(/\.mp4$/, ".webm")
    : null;
  return (
    <div className="mt-3 inline-block overflow-hidden rounded-sm border border-border bg-black">
      <video
        data-testid={`lesson-video-${step.id}`}
        controls
        playsInline
        preload="metadata"
        className="max-h-[460px] w-auto max-w-full bg-black"
        {...(webm ? {} : { src })}
      >
        {webm ? <source src={src} type="video/mp4" /> : null}
        {webm ? <source src={webm} type="video/webm" /> : null}
      </video>
    </div>
  );
};

const LessonModule = ({ mod }) => {
  const { state, updateState } = useData();
  const completed = state.completedSteps || [];
  const toggle = (id) =>
    updateState((prev) => {
      const list = prev.completedSteps || [];
      return {
        ...prev,
        completedSteps: list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
      };
    });

  return (
    <div className="border border-border bg-[#141414] rounded-sm" data-testid={`module-${mod.id}`}>
      <div className="border-b border-border px-5 py-4">
        <h3 className="display text-2xl">{mod.title}</h3>
        <p className="text-sm text-muted-foreground">{mod.summary}</p>
      </div>
      <ul>
        {(mod.steps || []).map((s) => {
          const done = completed.includes(s.id);
          return (
            <li
              key={s.id}
              className="flex items-start gap-4 border-b border-border/60 px-5 py-4 last:border-0 hover:bg-[#1d1d1d] transition-colors"
            >
              <button
                data-testid={`lesson-toggle-${s.id}`}
                onClick={() => toggle(s.id)}
                className="mt-0.5 text-primary"
                aria-label="toggle lesson"
              >
                {done ? <CheckCircle2 size={20} /> : <Circle size={20} className="text-muted-foreground" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className={`text-base font-semibold ${done ? "text-muted-foreground line-through" : ""}`}>
                  {s.title}
                </div>
                <div className="whitespace-pre-wrap text-sm text-muted-foreground">{s.summary}</div>
                {s.videoUrl ? <LessonVideo step={s} /> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const CertCard = ({ certKey }) => {
  const { content, state, updateState } = useData();
  const cert = content.certifications?.[certKey];
  if (!cert) return null;
  const checked = state.certifications?.[certKey] || [];
  const toggle = (id) =>
    updateState((prev) => {
      const list = prev.certifications?.[certKey] || [];
      return {
        ...prev,
        certifications: {
          ...(prev.certifications || {}),
          [certKey]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
        },
      };
    });

  return (
    <div
      className="border border-primary/40 bg-[#191603] rounded-sm p-5"
      data-testid={`cert-${certKey}`}
    >
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} className="text-primary" />
        <h3 className="display text-2xl">{cert.title}</h3>
        <span className="mono ml-auto text-[10px] tracking-widest text-primary">CHECKOFF REQUIRED</span>
      </div>
      <div className="mt-4 space-y-3">
        {cert.criteria.map((c) => (
          <label key={c.id} className="flex items-start gap-3 text-sm cursor-pointer">
            <Checkbox
              data-testid={`cert-item-${c.id}`}
              checked={checked.includes(c.id)}
              onCheckedChange={() => toggle(c.id)}
              className="mt-0.5 rounded-sm border-primary/60"
            />
            <span className={checked.includes(c.id) ? "text-muted-foreground" : ""}>{c.text}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export const TrainingTab = () => {
  const { content } = useData();
  const { paidAcademy } = useAccess();
  const roadmapIds = MILESTONES.filter((m) => m.type === "lessons").map((m) => m.moduleId);
  const ordered = [
    ...roadmapIds.map((id) => (content.modules || []).find((x) => x.id === id)).filter(Boolean),
    ...(content.modules || []).filter((m) => !roadmapIds.includes(m.id)),
  ];
  const visible = paidAcademy ? ordered : ordered.filter((m) => FREE_MODULE_IDS.includes(m.id));
  return (
    <div className="space-y-6" data-testid="training-tab">
      {visible.map((mod) => (
        <LessonModule key={mod.id} mod={mod} />
      ))}
      {!paidAcademy ? (
        <LockedPanel testId="locked-training" title="The rest of the training is locked" />
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <CertCard certKey="product" />
          <CertCard certKey="standard" />
        </div>
      )}
    </div>
  );
};
